import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaChevronDown } from 'react-icons/fa';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import DeliveryMap from '../components/DeliveryMap';
import PlaceAutocompleteInput from '../components/PlaceAutocompleteInput';
import quoteSqImg from '../assets/quote_sq.svg';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

// Main GetQuote content (rendered inside Wrapper)
const GetQuoteContent = () => {
  const navigate = useNavigate();
  const [deliveryType, setDeliveryType] = useState('Local');
  const [pickup, setPickup] = useState('');
  const [dropoffs, setDropoffs] = useState([{ value: '', place: null }]);
  const [pickupPlace, setPickupPlace] = useState(null);
  const [simulationResult, setSimulationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageError, setImageError] = useState(false);
  const [country, setCountry] = useState('');
  const [showMap, setShowMap] = useState(false);
  const mobileResultRef = useRef(null);

  const resetLocalForm = () => {
    setPickup('');
    setDropoffs([{ value: '', place: null }]);
    setPickupPlace(null);
    setSimulationResult(null);
    setError('');
    setShowMap(false);
  };

  const formatDuration = (minutesInput) => {
    if (minutesInput === undefined || minutesInput === null) return '';
    const minutes = Math.round(minutesInput);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h${mins > 0 ? ` ${mins} min` : ''}`;
  };

  const formatDistance = (kmInput) => {
    if (kmInput === undefined || kmInput === null) return '-';
    const roundedUp = Math.ceil(kmInput * 10) / 10;
    return `${roundedUp.toFixed(1)} km`;
  };

  const handleSimulate = async (e) => {
    e.preventDefault();
    setError('');
    setSimulationResult(null);

    if (deliveryType !== 'Local') {
      setError('Les livraisons internationales arrivent bientôt.');
      return;
    }

    if (!country) {
      setError('Merci de sélectionner un pays avant de renseigner les points.');
      return;
    }

    const allFilled = dropoffs.every((d) => d.value && d.value.trim().length > 0);
    const allGeocoded = pickupPlace?.lat && pickupPlace?.lng && dropoffs.every((d) => d.place?.lat && d.place?.lng);

    if (!pickup || !allFilled) {
      setError('Merci de renseigner tous les points.');
      return;
    }
    if (!allGeocoded) {
      setError('Veuillez choisir des suggestions pour obtenir les coordonnées.');
      return;
    }

    const deliveryPoints = dropoffs.map((d) => ({
      referenceId: d.place?.name || d.place?.address || d.value,
      plat: pickupPlace.lat,
      plng: pickupPlace.lng,
      dlat: d.place.lat,
      dlng: d.place.lng,
    }));

    const payload = {
      deliveryPoints,
      rideTypeId: 1,
      productTypeId: 1,
    };

    try {
      setLoading(true);
      setShowMap(false);
      const apiBase = process.env.REACT_APP_API_ENDPOINT;
      const res = await fetch(`${apiBase}/v1/public/tariff/simulate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`Erreur ${res.status}`);
      }

      const data = await res.json();
      console.log(JSON.stringify(data));
      setSimulationResult(data);
      setShowMap(true);
      
      // Auto-scroll to mobile result on small screens
      setTimeout(() => {
        if (mobileResultRef.current && window.innerWidth < 1024) {
          mobileResultRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 100);
    } catch (err) {
      setError(err.message || 'Erreur lors de la simulation');
    } finally {
      setLoading(false);
    }
  };

  // Reset places when country changes
  useEffect(() => {
    setPickup('');
    setDropoffs([{ value: '', place: null }]);
    setPickupPlace(null);
    setSimulationResult(null);
    setError('');
  }, [country]);

  const inputClassName = "w-full bg-gray-100 rounded-lg py-3.5 pl-12 pr-10 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black transition-all";

  const handleDropoffChange = (index, newValue) => {
    setSimulationResult(null);
    setError('');
    setShowMap(false);
    setDropoffs((prev) =>
      prev.map((item, i) => (i === index ? { ...item, value: newValue } : item))
    );
  };

  const handleDropoffSelect = (index, place) => {
    setSimulationResult(null);
    setError('');
    setShowMap(false);
    setDropoffs((prev) =>
      prev.map((item, i) => (i === index ? { ...item, place, value: place.name || place.address || item.value } : item))
    );
  };

  const addDropoff = () => {
    setDropoffs((prev) => {
      if (prev.length >= 5) return prev;
      return [...prev, { value: '', place: null }];
    });
    // Ne pas réinitialiser la simulation ni cacher la carte lors de l'ajout
  };

  const removeDropoff = (index) => {
    setDropoffs((prev) => prev.filter((_, i) => i !== index));
    // Ne pas réinitialiser la simulation ni cacher la carte lors de la suppression
    setError('');
  };

  // Lancer la course réellement : redirige vers QuickDelivery avec les adresses pré-remplies.
  // La simulation peut avoir plusieurs points de livraison ; on garde le premier.
  const handleOrder = () => {
    const firstDrop = dropoffs[0];
    navigate('/quick-delivery', {
      state: {
        country,
        pickup: { value: pickup, place: pickupPlace },
        dropoff: firstDrop ? { value: firstDrop.value, place: firstDrop.place } : null,
      },
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
          
          {/* Left Column - Form */}
          <div className="w-full lg:w-1/2 space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                Estimez votre livraison avec OuiGoo
              </h1>
              <p className="text-gray-600 text-lg">
                {deliveryType === 'Local' ? (
                  country
                    ? <>Obtenez un tarif instantané pour vos envois partout au <span className="font-semibold text-primary-custom">{country}</span></>
                    : <>Obtenez un tarif instantané pour vos envois partout</>
                ) : (
                  <>Obtenez un tarif instantané pour vos envois <span className="font-semibold text-primary-custom">à l’international</span></>
                )}
              </p>
            </div>

            <form onSubmit={handleSimulate} className="space-y-4 max-w-md">
              {/* Delivery Type Selector */}
              <div className="flex p-1 bg-gray-100 rounded-lg mb-4 w-fit">
                <button
                  type="button"
                  onClick={() => setDeliveryType('Local')}
                  className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                    deliveryType === 'Local'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  Local
                </button>
                <button
                  type="button"
                  onClick={() => setDeliveryType('International')}
                  className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                    deliveryType === 'International'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  International
                </button>
              </div>

              {/* Time Selector (Mock) */}
              {/* <div className="inline-flex items-center space-x-2 bg-gray-100 rounded-full px-4 py-2 mb-4 cursor-pointer hover:bg-gray-200 transition-colors">
                <FaClock className="w-4 h-4 text-gray-700" />
                <span className="text-sm font-medium text-gray-700">Départ immédiat</span>
              </div> */}

              {/* Country dropdown (local only) */}
              {deliveryType === 'Local' && (
                <div className="mb-2 relative">
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full bg-gray-100 rounded-lg px-4 pr-10 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-black appearance-none cursor-pointer"
                  >
                    <option value="" disabled>
                      Sélectionnez un pays
                    </option>
                    <option value="Benin">🇧🇯 Bénin</option>
                    <option value="Togo">🇹🇬 Togo</option>
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                    <FaChevronDown className="w-3 h-3" />
                  </div>
                </div>
              )}

              {deliveryType === 'International' ? (
                <div className="p-4 rounded-xl border border-dashed border-gray-300 bg-gray-50">
                  <p className="text-gray-800 font-semibold mb-1">International</p>
                  <p className="text-gray-600 text-sm">Disponible très bientôt. Restez connecté !</p>
                </div>
              ) : (
                <div className="relative space-y-2">
                  {/* Connecting Line */}
                  <div className="absolute left-7 top-10 bottom-10 w-0.5 bg-gray-300 z-0"></div>

                  {/* Pickup Input with Autocomplete */}
                  <PlaceAutocompleteInput
                    value={pickup}
                    onChange={(v) => { setPickup(v); setSimulationResult(null); setError(''); setShowMap(false); }}
                    onPlaceSelect={(place) => { setPickupPlace(place); setSimulationResult(null); setError(''); setShowMap(false); }}
                    placeholder="Lieu de ramassage"
                    country={country}
                    icon={<div className="w-2.5 h-2.5 rounded-full bg-black"></div>}
                    inputClassName={inputClassName}
                    disabled={!country}
                  />

                  {/* Dynamic Dropoff Inputs */}
                  {dropoffs.map((drop, index) => (
                    <div key={index} className="relative">
                      <PlaceAutocompleteInput
                        value={drop.value}
                        onChange={(v) => handleDropoffChange(index, v)}
                        onPlaceSelect={(place) => handleDropoffSelect(index, place)}
                        placeholder={
                          dropoffs.length === 1
                            ? 'Lieu de livraison'
                            : `Lieu de livraison ${index + 1}`
                        }
                        country={country}
                        icon={<div className="w-2.5 h-2.5 bg-black"></div>}
                        inputClassName={inputClassName}
                        disabled={!country}
                      />
                      {dropoffs.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeDropoff(index)}
                          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full w-8 h-8 text-sm text-gray-600 hover:text-red-500 hover:border-red-200 transition flex items-center justify-center"
                          aria-label="Supprimer ce point de livraison"
                          disabled={!country}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addDropoff}
                    disabled={!country}
                    className={`mt-3 inline-flex items-center text-sm font-medium underline underline-offset-4 ${
                      !country ? 'text-gray-400 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Ajouter un point de livraison
                  </button>
                </div>
              )}

              {/* Action Buttons (desktop keeps them here; on mobile after a result they move
                  to the bottom, under the map + details) */}
              <div className={`pt-4 flex-col sm:flex-row items-stretch sm:items-center gap-3 ${simulationResult ? 'hidden lg:flex' : 'flex'}`}>
                <button
                  type="submit"
                  disabled={loading}
                  className={`px-8 py-3.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    loading
                      ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                      : simulationResult
                        ? 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                        : 'bg-black text-white hover:bg-gray-800'
                  }`}
                >
                  {loading ? 'Calcul en cours...' : simulationResult ? 'Recalculer' : 'Voir les prix'}
                </button>
                {simulationResult && (
                  <button
                    type="button"
                    onClick={handleOrder}
                    className="px-8 py-3.5 rounded-lg font-medium bg-black text-white hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                  >
                    Commander cette course <FaArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </form>

            {/* Errors */}
            {error && (
              <div className="mt-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-100">
                {error}
              </div>
            )}
          </div>

          {/* Right Column - Map or Illustration */}
          <div className="w-full lg:w-1/2 relative">
            {showMap && simulationResult && pickupPlace ? (
              /* Google Maps with delivery routes */
              <div className="aspect-square rounded-3xl overflow-hidden bg-gray-100 relative">
                <DeliveryMap 
                  simulationResult={simulationResult} 
                  pickupPlace={pickupPlace}
                  dropoffs={dropoffs}
                  pickupName={pickup.value}
                />

                {/* Simulation Details Overlay (desktop) */}
                {showMap && simulationResult && (
                  <div className="absolute left-4 right-4 bottom-4 bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100 shadow-lg p-4 space-y-3 hidden lg:block">
                    {/* <h3 className="text-lg font-semibold">Estimation</h3> */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                        <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Tarif total</p>
                        <p className="text-lg font-bold text-gray-900">{simulationResult.grandTotalTariff ?? '-'} FCFA</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                        <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Distance totale</p>
                        <p className="text-lg font-bold text-gray-900">
                          {simulationResult.grandTotalDistance ? formatDistance(simulationResult.grandTotalDistance) : '-'}
                        </p>
                      </div>
                      <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                        <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Durée totale</p>
                        <p className="text-lg font-bold text-gray-900">
                          {simulationResult.grandTotalDuration ? formatDuration(simulationResult.grandTotalDuration) : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            ) : (
              /* Default illustration when no simulation */
              <div className="aspect-square rounded-3xl overflow-hidden bg-gray-100 relative">
                {!imageError ? (
                  <img
                    src={quoteSqImg}
                    alt="Illustration simulation"
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-tr from-orange-100 via-orange-50 to-blue-50">
                    <div className="text-4xl text-orange-500 font-bold opacity-20">OuiGoo</div>
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Mobile only: details under the map, then actions at the very bottom */}
          {showMap && simulationResult && (
            <div className="w-full space-y-4 lg:hidden">
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-3">
                <h3 className="text-lg font-semibold">Estimation</h3>
                <div className="grid grid-cols-3 gap-2">
                  <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Tarif total</p>
                    <p className="text-base font-bold text-gray-900">{simulationResult.grandTotalTariff ?? '-'} FCFA</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Distance totale</p>
                    <p className="text-base font-bold text-gray-900">
                      {simulationResult.grandTotalDistance ? formatDistance(simulationResult.grandTotalDistance) : '-'}
                    </p>
                  </div>
                  <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Durée totale</p>
                    <p className="text-base font-bold text-gray-900">
                      {simulationResult.grandTotalDuration ? formatDuration(simulationResult.grandTotalDuration) : '-'}
                    </p>
                  </div>
                </div>
              </div>
              <div ref={mobileResultRef} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 scroll-mb-6">
                <button
                  type="button"
                  onClick={handleSimulate}
                  disabled={loading}
                  className={`px-8 py-3.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    loading ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {loading ? 'Calcul en cours...' : 'Recalculer'}
                </button>
                <button
                  type="button"
                  onClick={handleOrder}
                  className="px-8 py-3.5 rounded-lg font-medium bg-black text-white hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                >
                  Commander cette course <FaArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

        </div>

        {/* Note explicative IA */}
        <div className="mt-10 text-sm text-gray-600">
          <p className="font-semibold text-gray-800 mb-1">Optimisation intelligente</p>
          <p>
            Nous utilisons un moteur d’optimisation alimenté par l’IA pour calculer l’itinéraire le plus rapide et regrouper vos livraisons. Cela réduit les détours, améliore la ponctualité et limite les coûts de chaque trajet.
          </p>
        </div>
      </div>
    </div>
  );
};

// Render function for Wrapper
const renderStatus = (status) => {
  if (status === Status.LOADING) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }
  if (status === Status.FAILURE) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-red-500">Erreur de chargement de Google Maps</p>
      </div>
    );
  }
  return null;
};

// Main GetQuote component with Google Maps Wrapper
const GetQuote = () => {
  return (
    <Wrapper 
      apiKey={GOOGLE_MAPS_API_KEY} 
      libraries={['places', 'marker']}
      render={renderStatus}
    >
      <GetQuoteContent />
    </Wrapper>
  );
};

export default GetQuote;
